import ErrorMessage from "@/v2-ui/ErrorMessage"
import Icon from "@/v2-ui/icon"
import { useUserMfaQuery } from "@/v2-backoffice-client/user/mfa/user.mfa.query"
import UserMfaMethodCard from "@/v2-backoffice-client/user/mfa/UserMfaMethodCard"
import UserMfaAddPhone from "@/v2-backoffice-client/user/mfa/UserMfaAddPhone"
import UserMfaAuditLogList from "@/v2-backoffice-client/user/mfa/UserMfaAuditLogList"
import UserResetMfaButton from "@/v2-backoffice-client/user/resetMfa/UserResetMfaButton"

export type UserMfaProps = {
  userId: number
}

// MFA tab: the user's registered MFA methods (with admin overrides) plus their
// recent MFA-related audit log, all fetched live from MS Graph on open. The
// overrides exist because shared-account holders kept registering each other's
// numbers and locking the rest out.
function UserMfa(props: UserMfaProps) {
  const { userId } = props

  const { data, loading, error, refetch } = useUserMfaQuery({
    variables: { id: userId },
    fetchPolicy: "cache-and-network",
  })

  const methods = data?.getUserMfa?.Methods ?? []
  const auditLogs = data?.getUserMfa?.AuditLogs ?? []
  const hasMethods = methods.length > 0

  return (
    <div className="flex max-w-2xl flex-col gap-6 p-4">
      <ErrorMessage error={error} />

      <section className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              Registered MFA methods
              <span className="rounded-full bg-muted px-1.5 text-xs font-normal text-muted-foreground tabular-nums">
                {methods.length}
              </span>
            </h3>
            <p className="text-xs text-muted-foreground">
              Phone numbers this user receives sign-in codes on. Fix a wrong
              number, add a working one, or reset everything so they re-register
              themselves.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <UserMfaAddPhone
              userId={userId}
              onCompleted={() => {
                void refetch()
              }}
            />
            {hasMethods && (
              <UserResetMfaButton
                userId={userId}
                size="sm"
                onCompleted={() => {
                  void refetch()
                }}
              />
            )}
          </div>
        </div>

        {loading && !hasMethods ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !hasMethods ? (
          <div className="flex flex-col items-center gap-1 rounded-md border border-dashed px-3 py-6 text-center">
            <Icon
              src="shield-check"
              size={20}
              className="text-muted-foreground"
            />
            <p className="text-sm font-medium">No MFA methods registered</p>
            <p className="text-xs text-muted-foreground">
              The user will be prompted to enrol on their next sign-in.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {methods.map((method) => (
              <UserMfaMethodCard
                key={method.Id}
                userId={userId}
                method={method}
                onChanged={() => {
                  void refetch()
                }}
              />
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">Recent MFA activity</h3>
        {loading && auditLogs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <UserMfaAuditLogList logs={auditLogs} />
        )}
      </section>
    </div>
  )
}

export default UserMfa
